import React, { useEffect, useState, useMemo } from 'react';
import { Row, Button, FormGroup, Form, Col, Alert } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import * as dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
    getBookingDetails,
    useBookingState,
    useAccountState,
    getTripSettings,
    getBookingVehiclesDetails,
    useAddonAssignmentState,
    getAddonAssignments,
    getBookingDiscounts,
    useInvoicesState,
    getInvoiceDetails,
} from '../../../../Context';
import { printSignedBookingService, bookingConfirmation } from '../../../../services/bookingsService';
import { getBookingTransaction } from '../../../../Context/actions/bookingsActions';
import { getCustomerDetails } from '../../../../config';

import Loading from '../../../../Components/Loading';
import { InfoField } from '../../../../Components/Reusable';
import SimpleTable from '../../../../Components/SimpleTable';
import MockupLayout from '../../../../Components/MockupLayout';
import CustomAccordion from '../../../../Components/CustomAccordion';
import BookingAssignment from '../../../../Components/BookingAssignment';
import {
    formatCurrency,
    calculateTotal,
    formatDateTimeWithDayAbbr,
    convertQueryStringToObject,
} from '../../../../utils/helper';

dayjs.extend(utc);
dayjs.extend(timezone);

const BookingDetails = (props) => {
    const bookingId = props.match.params.id;
    const queryObj = convertQueryStringToObject(props.location.search);
    const [loading, setLoading] = useState(false);
    const [collapseItem, handleCollapse] = useState({ 1: false });

    const {
        bookingsDispatch,
        bookings: { bookingDetails, vehicles, discounts, transactions },
    } = useBookingState();
    const {
        AccountsDispatch,
        tripSettings: { tripSettings },
    } = useAccountState();
    const {
        addonAssignmentDispatch,
        addonAssignment: { addonAssignments },
    } = useAddonAssignmentState();
    const {
        invoiceDispatch,
        invoice: { invoiceDetails },
    } = useInvoicesState();

    useEffect(() => {
        let isMounted = true;
        (async () => {
            setLoading(true);
            try {
                await getTripSettings(AccountsDispatch);
                const data = await getBookingDetails(bookingsDispatch, bookingId);
                const tasks = [
                    getBookingVehiclesDetails(bookingsDispatch, bookingId),
                    getBookingDiscounts(bookingsDispatch, bookingId),
                    getAddonAssignments(addonAssignmentDispatch, { bookingId, isStoreProc: true }),
                    getBookingTransaction(bookingsDispatch, bookingId),
                ];
                if (data?.details?.invoiceID) tasks.push(getInvoiceDetails(invoiceDispatch, data.details.invoiceID));

                await Promise.all(tasks);
            } catch (error) {
                console.error('Error fetching booking details', error);
            } finally {
                if (isMounted) setLoading(false);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, [bookingsDispatch, bookingId, addonAssignmentDispatch, AccountsDispatch, invoiceDispatch]);

    const handleItemCollapse = (e) => {
        const { id } = e.target;
        handleCollapse((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const onVehicleClick = (item) => {
        const originParams =
            queryObj.backTo && queryObj.backUrl
                ? `&originBackTo=${encodeURIComponent(queryObj.backTo)}&originBackUrl=${encodeURIComponent(queryObj.backUrl)}`
                : '';
        props.history.push(`/bookings-vehicle-assignment/${item.id}?bookingId=${bookingId}${originParams}`);
    };

    const handleCheckOutPageClick = () => {
        const timestamp = new Date(bookingDetails?.createdAt).valueOf();
        window.open(`${window.location.origin}/p/booking-payment?bid=${bookingId}&ts=${timestamp}`, '_blank');
    };

    const handlePrintContract = async () => {
        try {
            setLoading(true);
            const pdfData = await printSignedBookingService(bookingId, '', '', '', 1);
            setLoading(false);

            const blob = new Blob([pdfData.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `Booking (${bookingDetails.bookingId})`;
            link.click();
        } catch (e) {
            setLoading(false);
            Swal.fire({ title: 'Not Found', text: 'No Contract Found!', icon: 'error' });
        }
    };

    const handleConfirmDetails = async () => {
        const confirm = await Swal.fire({
            title: 'Confirm Booking Details',
            text: 'Are you sure you would like to confirm the details of this booking?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, confirm',
        });
        if (!confirm.isConfirmed) return;

        setLoading(true);
        try {
            const customer = getCustomerDetails();
            const customerName = `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim();
            await bookingConfirmation(bookingId, customerName);
            await getBookingDetails(bookingsDispatch, bookingId);
        } catch (error) {
            console.error('Error confirming booking details', error);
        } finally {
            setLoading(false);
        }
    };

    const totalRenderer = (data, key) => `$${formatCurrency(calculateTotal(data, key))}`;

    const getRowStyle = (item) => {
        if (item.refundedAt) return { backgroundColor: '#CBC3E3' };
        if (item.cancelledAt) return { backgroundColor: '#f08080' };
        return { backgroundColor: '#FFFFFF' };
    };

    const displayTransactionRef = (item) => item.fiservTransactionRef || item.payrocTransactionRef || '';

    const vehicleColumns = useMemo(() => {
        let cols = [['routeDescription', 'Route Description', null, 'TOTALS']];

        if (tripSettings.cp_showVehicleType) cols.push(['vehicleType', 'Vehicle Type']);
        if (tripSettings.cp_showVehicleId) cols.push(['vehicleID', 'Vehicle ID']);

        const showDriverDetailsButton = bookingDetails.showDriverDetailsButton;
        const showFirstName = tripSettings.cp_showDriverFirstName;
        const showLastName = tripSettings.cp_showDriverLastName;
        const showInitial = tripSettings.cp_showDriverLastInitial;

        if ((showFirstName || showLastName || showInitial) && showDriverDetailsButton) {
            cols.push([
                'driver',
                'Driver',
                (item) => {
                    const first = showFirstName && item.driverFirstName ? `${item.driverFirstName} ` : '';
                    const last = showLastName && item.driverLastName ? `${item.driverLastName} ` : '';
                    const initial = showInitial && item.driverLastInitial ? `${item.driverLastInitial} ` : '';
                    return first + last + initial;
                },
            ]);
        }

        if (tripSettings.cp_showDriverPhoneNumber && showDriverDetailsButton) {
            cols.push(['mobilePhone', 'Driver Mobile Phone Number']);
        }

        const isNotDocumentType2 = bookingDetails.documentTypeID !== 2;

        if (isNotDocumentType2) {
            cols.push(
                [
                    'price',
                    'Vehicle Price',
                    (item) => `$${formatCurrency(item['price'])}`,
                    totalRenderer(vehicles, 'price'),
                ],
                [
                    'salesTax',
                    'Tax',
                    (item) => `$${formatCurrency(item['salesTax'])}`,
                    totalRenderer(vehicles, 'salesTax'),
                ],
                ['taxDescription', 'Tax Description']
            );
        }
        return cols;
    }, [bookingDetails, tripSettings, vehicles]);

    return (
        <MockupLayout
            title={`Booking ${bookingDetails?.bookingId || 'Details'} ${bookingDetails.cancelledAt ? '(Cancelled)' : ''}`}
            titleIconClass={'fas fa-calendar-check'}
            textColor={tripSettings?.textColor}
            buttons={
                <Button
                    onClick={() => props.history.push(queryObj.backUrl || `/bookings`)}
                    style={{ backgroundColor: `${tripSettings.buttonColor}`, borderColor: 'transparent' }}
                >
                    {queryObj.backTo ? `BACK TO ${queryObj.backTo.toUpperCase()}` : 'BACK TO BOOKINGS'}
                </Button>
            }
        >
            {/* --- Trip Details Section --- */}
            <CustomAccordion
                id={1}
                title="Trip Details"
                collapseItem={collapseItem}
                handleItemCollapse={handleItemCollapse}
                sectionColor={tripSettings?.sectionColor}
                className="px-2"
            >
                {/* Contact Info */}
                <Row className="mx-1">
                    <InfoField label="Primary Contact" value={bookingDetails.primaryContactFullName} />
                    <InfoField label="Billing Contact" value={bookingDetails.billingContactFullName} />
                </Row>

                {/* Staff / Quote Info */}
                <Row className="mx-1">
                    <InfoField label="Associated Quote ID #" value={bookingDetails.bookingQuoteId} />
                    <InfoField label="Operations Contact" value={bookingDetails.operationalContactFullName} />
                    <InfoField label="Salesperson" value={bookingDetails.salesPerson} />
                </Row>

                {/* Reference / Group Info */}
                <Row className="mx-1">
                    <InfoField label="Trip Reference" value={bookingDetails.tripReference} colClass="col-12 col-md-3" />
                    <InfoField label="Group Name" value={bookingDetails.groupName} colClass="col-12 col-md-3" />
                    <InfoField
                        label="Purchase Order #"
                        value={bookingDetails.purchaseOrder}
                        colClass="col-12 col-md-3"
                    />

                    {/* Action Buttons */}
                    {bookingDetails.isSignedContract !== 0 && (
                        <InfoField
                            colClass="col-12 col-md-3"
                            children={
                                <div style={{ alignSelf: 'center', textAlign: 'center' }}>
                                    <Button
                                        onClick={handlePrintContract}
                                        style={{
                                            backgroundColor: tripSettings.buttonColor,
                                            borderColor: 'transparent',
                                        }}
                                    >
                                        View Signed Contract
                                    </Button>
                                </div>
                            }
                        />
                    )}
                    {bookingDetails.showDriverDetailsButton !== 0 && (
                        <InfoField
                            colClass="col-12 col-md-3"
                            children={
                                <div style={{ alignSelf: 'center', textAlign: 'center' }}>
                                    <Button
                                        onClick={() =>
                                            props.history.push(
                                                `/p/bookings/driver-details/${bookingId}?ts=${new Date(
                                                    bookingDetails?.createdAt
                                                ).valueOf()}`
                                            )
                                        }
                                        style={{
                                            backgroundColor: tripSettings.buttonColor,
                                            borderColor: 'transparent',
                                        }}
                                    >
                                        Driver Details
                                    </Button>
                                </div>
                            }
                        />
                    )}
                    {!bookingDetails.cancelledAt &&
                        ((bookingDetails.detailsConfirmedByCustomerName &&
                            bookingDetails.detailsConfirmedByCustomerAt) ||
                            bookingDetails.showConfirmDetailsButton) && (
                            <InfoField
                                colClass="col-12 col-md-3"
                                children={
                                    <div style={{ alignSelf: 'center', textAlign: 'center' }}>
                                        {bookingDetails.detailsConfirmedByCustomerName &&
                                        bookingDetails.detailsConfirmedByCustomerAt ? (
                                            <span>
                                                Details confirmed by{' '}
                                                <b>{bookingDetails.detailsConfirmedByCustomerName}</b> on{' '}
                                                <b>
                                                    {formatDateTimeWithDayAbbr(
                                                        bookingDetails.detailsConfirmedByCustomerAt
                                                    )}
                                                </b>
                                            </span>
                                        ) : (
                                            <Button
                                                onClick={handleConfirmDetails}
                                                style={{
                                                    backgroundColor: tripSettings.buttonColor,
                                                    borderColor: 'transparent',
                                                }}
                                            >
                                                Confirm Details
                                            </Button>
                                        )}
                                    </div>
                                }
                            />
                        )}
                </Row>
            </CustomAccordion>

            {/* --- Vehicles Table --- */}
            <CustomAccordion
                id={4}
                title={'VEHICLES'}
                collapseItem={collapseItem}
                handleItemCollapse={handleItemCollapse}
                sectionColor={tripSettings?.sectionColor}
            >
                <SimpleTable
                    data={vehicles || []}
                    onClick={() => {}}
                    bordered
                    mainColor={tripSettings?.mainColor}
                    showFooter={bookingDetails.documentTypeID !== 2}
                    fieldNames={vehicleColumns}
                />
            </CustomAccordion>

            {/* --- Addons Table --- */}
            {bookingDetails.documentTypeID !== 2 && (
                <CustomAccordion
                    id={5}
                    title={'ADDONS'}
                    collapseItem={collapseItem}
                    handleItemCollapse={handleItemCollapse}
                    sectionColor={tripSettings?.sectionColor}
                >
                    <SimpleTable
                        data={addonAssignments}
                        bordered
                        mainColor={tripSettings?.mainColor}
                        showFooter={true}
                        fieldNames={[
                            [
                                'description',
                                'Description',
                                (item) => item.overrideDescription || item.description,
                                'TOTALS',
                            ],
                            ['cost', 'Cost'],
                            ['quantity', 'Quantity'],
                            [
                                'price',
                                'Addon Price',
                                (item) => `$${formatCurrency(item['price'])}`,
                                totalRenderer(addonAssignments, 'price'),
                            ],
                            [
                                'salesTax',
                                'Tax',
                                (item) => `$${formatCurrency(item['salesTax'])}`,
                                totalRenderer(addonAssignments, 'salesTax'),
                            ],
                            ['salesTaxDescription', 'Tax Description'],
                        ]}
                    />
                </CustomAccordion>
            )}

            {/* --- Discounts Table --- */}
            <CustomAccordion
                id={10}
                title={'DISCOUNTS'}
                collapseItem={collapseItem}
                handleItemCollapse={handleItemCollapse}
                sectionColor={tripSettings?.sectionColor}
            >
                <SimpleTable
                    data={discounts}
                    mainColor={tripSettings?.mainColor}
                    bordered
                    showFooter={true}
                    fieldNames={[
                        ['description', 'Description', null, 'TOTALS'],
                        ['code', 'Code'],
                        ['fAmount', 'Amount', null, totalRenderer(discounts, 'amount')],
                    ]}
                />
            </CustomAccordion>

            {/* --- Route Visualization --- */}
            <CustomAccordion
                id={2}
                key={2}
                collapseItem={collapseItem}
                handleItemCollapse={handleItemCollapse}
                sectionColor={tripSettings?.sectionColor}
                title="Routes"
            >
                <div className="px-md-4 py-2 overflow-auto">
                    <BookingAssignment
                        cancelled={bookingDetails.cancelledAt}
                        mainColor={tripSettings?.mainColor}
                        bookingId={bookingId}
                        onRowClick={onVehicleClick}
                    />
                </div>
            </CustomAccordion>

            {/* --- Financial Section --- */}
            <CustomAccordion
                id={3}
                title="Financial"
                handleItemCollapse={handleItemCollapse}
                sectionColor={tripSettings?.sectionColor}
                collapseItem={collapseItem}
            >
                {bookingDetails?.holdInvoice !== 0 &&
                    bookingDetails?.displayHoldInvoiceMessage !== 0 &&
                    bookingDetails?.holdInvoiceText && (
                        <Alert variant="warning">{bookingDetails.holdInvoiceText}</Alert>
                    )}
                {bookingDetails?.invoiceID ? (
                    <Row>
                        <Col>
                            <Form.Label>View Invoice :</Form.Label>
                            <Link to={`/invoices/${bookingDetails.invoiceID}`}>
                                <span className="font-weight-bold ml-2">
                                    {invoiceDetails?.invoiceNumber}
                                    <i className="fa fa-link ml-2" />
                                </span>
                            </Link>
                        </Col>
                    </Row>
                ) : (
                    <>
                        <Row className="mx-1">
                            {/* Payment Info */}
                            <div className="col-6 col-md-3 details-list">
                                <InfoField label="Payment Status" value={bookingDetails.paymentStatus} colClass="" />
                                <InfoField label="Deposit Due Date" value={bookingDetails.depositDueDate} colClass="" />
                                <InfoField label="Deposit Amount" value={bookingDetails.depositAmount} colClass="" />
                            </div>

                            {/* Due Dates & Totals */}
                            <div className="col-6 col-md-3 details-list">
                                <InfoField
                                    label="Final Payment Due Date"
                                    value={bookingDetails.finalPaymentDueDate}
                                    colClass=""
                                />
                                <InfoField label="Total Before Tax" value={bookingDetails.totalBeforeTax} colClass="" />
                            </div>

                            {/* Tax & Paid */}
                            <div className="col-6 col-md-3 details-list">
                                <InfoField label="Total Tax" value={bookingDetails.totalTax} colClass="" />
                                <InfoField label="Amount Paid" value={bookingDetails.amountPaid} colClass="" />
                            </div>

                            {/* Outstanding & Payment Button */}
                            <div className="col-6 col-md-3 details-list">
                                <InfoField label="Total After Tax" value={bookingDetails.totalAfterTax} colClass="" />
                                <InfoField
                                    label="Amount Outstanding"
                                    value={bookingDetails.amountOutstanding}
                                    colClass=""
                                />

                                {bookingDetails?.amountOutstanding > 0 &&
                                    !bookingDetails.disablePayment &&
                                    Number(bookingDetails.paymentTermID) === 1 && (
                                        <FormGroup>
                                            <Form.Label>Make Payment</Form.Label>
                                            <dd>
                                                <Button
                                                    style={{
                                                        backgroundColor: tripSettings.buttonColor,
                                                        borderColor: 'transparent',
                                                    }}
                                                    onClick={handleCheckOutPageClick}
                                                >
                                                    Make Payment
                                                </Button>
                                            </dd>
                                        </FormGroup>
                                    )}
                            </div>
                        </Row>

                        <Row className="my-2">
                            <Col xs={12}>
                                <h6>Transactions</h6>
                                <SimpleTable
                                    data={transactions}
                                    mainColor={tripSettings?.mainColor}
                                    onClick={() => {}}
                                    rowStyle={getRowStyle}
                                    fieldNames={[
                                        ['transactionTypeDescription', 'Transaction Type'],
                                        ['transactionRefNumber', 'Transaction Reference'],
                                        ['transactionRef', 'Transaction ID', displayTransactionRef],
                                        ['amount', 'Amount', (item) => `$${formatCurrency(item['amount'])}`],
                                        ['date', 'Date'],
                                    ]}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </CustomAccordion>

            {/* --- Loaders --- */}
            {loading && <Loading loading={loading} />}
        </MockupLayout>
    );
};

export default BookingDetails;
